import React, {useRef, useState} from "react";
import {useData} from "../../../hooks/useData";
import {AclUser, GetAclUsersResponse} from "../interfaces";
import {useServerId} from "../useServerId";
import OverlayBase from "../overlay/OverlayBase";
import {doFetch} from "../../../util/fetch";
import {useWatcher} from "../../../hooks/useWatcher";
import Button from "../../../components/Button";
import {LucideCrown} from "lucide-react";
import {useOverlay} from "../overlay/OverlayContext";

export default function Moderators(){

    const selectId = useRef<string>()
    const [selectedUser, setSelectedUser] = useState<AclUser>()
    const [showModeratorOverlay, setShowModeratorOverlay] = useState<boolean>(false)

    const serverId = useServerId();
    const [n, setN] = useState(0);
    const data = useData<GetAclUsersResponse>(`/api/client/acl/users?serverId=${serverId}&n=${n}`);

    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState<string>()

    useWatcher(data, data => {
        if (data.data && selectId.current) {
            const user = data.data.users.find(u => u.id === selectId.current);
            if (user) {
                selectId.current = undefined
                selectUser(user)
            }
        }
    })

    const selectUser = (user: AclUser) => {
        setSelectedUser({...user})
    }

    const doSave = async () => {
        if (!selectedUser){
            return
        }

        setSaveError("")
        setSaving(true)

        const response = await doFetch(`/api/client/acl/user?serverId=${serverId}`, {
            method: "PUT",
            headers: {
                "Content-Type" : "application/json"
            },
            body: JSON.stringify({
                userId: selectedUser.id,
                permissions: selectedUser.permissions
            })
        })

        setSaving(false)

        if (response.ok){
            setSelectedUser(undefined)
            setN(n => n + 1)
        }
        else{
            setSaveError(response.errorString ?? "Unknown error (-1)");
        }
    }

    return (<>
        {showModeratorOverlay && <AddModeratorOverlay hide={(steamId) => {
            if (steamId) {
                selectId.current = steamId
                setN(n => n + 1)
            }
            setShowModeratorOverlay(false)
        }}/>}
        <div className="size-full flex flex-row p-4 relative space-x-4 h-full">
            <div className="w-96 max-w-full rounded-md bg-neutral-800 h-full p-2 space-y-2 flex flex-col">
                <Button onClick={() => setShowModeratorOverlay(true)} text="Add Moderator" className="bg-primary hover:bg-primary-hover" />

                {data.data && data.data.users.map(u => <div key={u.id} onClick={() => {
                    selectId.current = undefined
                    setSelectedUser(u)
                }} className={`bg-neutral-${(selectedUser?.id === u.id) ? "600" : "700"} hover:bg-neutral-600 p-2 rounded-md cursor-pointer`}>
                    <h3 className="font-bold text-lg">{u.isOwner && <LucideCrown size={20} className="text-yellow-300 inline me-1 align-text-bottom" />}{u.displayName}</h3>
                    <p className="text-neutral-500">{u.steamId ?? <i>No steam id</i>}</p>
                </div>)}
            </div>
            {selectedUser && <div className="grow rounded-md bg-neutral-900 p-4 flex flex-col justify-between border-neutral-600 border">
                <div className="space-y-2">
                    <h2 className="font-bold text-xl mb-2">Permissions for {selectedUser.displayName}</h2>

                    {selectedUser.isOwner && <p className="font-bold text-orange-500">Permissions of the Server owner can not be changed!</p>}

                    <Permission name="socket" displayName="Connect to WebSocket (required to use RustServerTool)"/>
                    <Permission name="adlog_view" displayName="View audit log"/>
                    <Permission name="whitelist" displayName="Manage whitelist"/>
                    <Permission name="settings" displayName="Change server settings"/>

                    <PermissionGroup displayName="Console">
                        <Permission name="console_view" displayName="View console"/>
                        <Permission name="console_command" displayName="Run console commands"/>
                        <Permission name="console_filters" displayName="Manage console filters"/>
                    </PermissionGroup>

                    <PermissionGroup displayName="Players">
                        <Permission name="players_view" displayName="View players (including map)"/>
                        <Permission name="players_kick" displayName="Kick players"/>
                        <Permission name="players_ban" displayName="Manage bans"/>
                        <Permission name="players_ban" displayName="Manage IP bans"/>
                        <Permission name="players_notes" displayName="Manage player notes"/>
                        <Permission name="players_strikes" displayName="Manage player strikes"/>
                    </PermissionGroup>

                </div>
                <div className="flex justify-end">
                    <p className="text-red-500">{saveError}</p>
                    <Button text="Save" className="bg-green-500" onClick={doSave} loading={saving} disabled={selectedUser.isOwner} />
                </div>
            </div>}
            {/* FOR DYNAMIC CLASS NAMES */}
            <span className="hidden bg-neutral-600 bg-neutral-700"></span>
        </div>
    </>)

    function PermissionGroup(props: {displayName: string, children: React.ReactNode}) {
        return <div>
            <h3 className="text-lg font-medium mb-1">{props.displayName}</h3>
            <div className="flex flex-row flex-wrap">
                {props.children}
            </div>
        </div>
    }

    function Permission(props : {name: string, displayName?: string}) {
        if (!selectedUser){
            return null
        }

        const togglePerm = () => {
            if (selectedUser.permissions.includes(props.name)) {
                setSelectedUser({
                    ...selectedUser,
                    permissions: selectedUser.permissions.filter(x => x !== props.name)
                })
            }
            else{
                setSelectedUser({
                    ...selectedUser,
                    permissions: [...selectedUser.permissions, props.name]
                })
            }
        }

        return <div className="flex items-center space-x-2 me-4 mb-0.5">
            <input type="checkbox" id={`perm_${props.name}`} onChange={togglePerm}
                   checked={selectedUser.permissions.includes(props.name)} disabled={selectedUser.isOwner}
                   className="w-5 h-5 text-blue-600 border-neutral-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={`perm_${props.name}`}>{props.displayName ?? props.name}</label>
        </div>
    }
}

function AddModeratorOverlay(props: { hide: (selectSteamId?: string) => void }){

    const [loading, setLoading] = useState<boolean>(false)
    const [steamId, setSteamId] = useState<string>("")

    const [error, setError] = useState<string>("")
    const [validationError, setValidationError] = useState<string>("")

    const serverId = useServerId();
    const overlay = useOverlay()

    const handleSubmit = async () => {
        if (loading){
            return
        }
        setError("")
        setValidationError("")
        if (!steamId || !steamId.trim()) {
            setValidationError("Invalid steam id")
            return
        }
        setLoading(true)

        const response = await doFetch(`/api/client/acl/user?serverId=${serverId}`, {
            method: "POST",
            headers: {
                "Content-Type" : "application/json"
            },
            body: JSON.stringify({
                steamId: steamId
            })
        })

        setLoading(false)

        if (response.ok){
            props.hide(steamId)
        }
        else{
            if (response.error){
                overlay.openErrorOverlay(response.error)
            }
            setError(response.errorString ?? "Unknown error (-1)");
        }
    };

    return <OverlayBase hide={() => props.hide()} w="w-[28rem]">
        <h2 className="text-xl font-semibold mb-4">Add Moderator</h2>
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium">Steam ID</label>
                <input
                    type="number"
                    value={steamId}
                    onChange={(e) => setSteamId(e.target.value)}
                    placeholder="Enter SteamID64"
                    className="w-full rst-input"
                />
                {validationError && <p className="text-red-500 text-sm mt-1">{validationError}</p>}
            </div>
        </div>
        <div className="mt-6 flex flex-col items-end">
            {error && <p className="text-red-500 text-sm mb-2 text-right">{error}</p>}
            <div className="flex justify-end gap-3">
                <button onClick={() => props.hide()}
                        className="px-4 py-2 hover:bg-neutral-700 border border-neutral-500 rounded-lg">Cancel
                </button>
                <Button text="Add" className="bg-green-600" onClick={handleSubmit} loading={loading} />
            </div>
        </div>
    </OverlayBase>

}